/**
 * Observes an element's visibility and calls a callback when it changes.
 * Handles viewport intersection, display, and visibility CSS properties.
 *
 * @param element - The element to observe
 * @param callback - Called with true when visible, false when hidden
 * @returns Cleanup function to disconnect observers
 */
export function observeElementVisibility(
  element: HTMLElement,
  callback: (isVisible: boolean) => void,
): () => void {
  let isIntersecting = false;
  let lastVisibleState: boolean | undefined;

  const isStyleVisible = (): boolean => {
    // Check computed styles on element and all ancestors
    let currentElement: HTMLElement | null = element;
    while (currentElement) {
      const style = window.getComputedStyle(currentElement);
      if (style.display === 'none' || style.visibility === 'hidden') {
        return false;
      }
      currentElement = currentElement.parentElement;
    }
    return true;
  };

  const checkVisibility = () => {
    const styleVisible = isStyleVisible();
    const visible = isIntersecting && styleVisible;
    updateVisibilityState(visible);
  };

  const updateVisibilityState = (isVisible: boolean) => {
    if (lastVisibleState !== isVisible) {
      lastVisibleState = isVisible;
      callback(isVisible);
    }
  };

  const intersectionObserver = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        isIntersecting = entry.isIntersecting;
        checkVisibility();
      });
    },
    { threshold: 0 },
  );

  const mutationObserver = new MutationObserver(() => {
    checkVisibility();
  });

  // Observe the element and all ancestors for visibility changes
  let ancestorElement: HTMLElement | null = element;
  while (ancestorElement && ancestorElement !== document.body) {
    mutationObserver.observe(ancestorElement, {
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden'],
    });
    ancestorElement = ancestorElement.parentElement;
  }

  intersectionObserver.observe(element);

  return () => {
    intersectionObserver.disconnect();
    mutationObserver.disconnect();
  };
}
